import { useCallback, useMemo } from 'react';

import { type PartFileUnits } from '@frontend/api';
import {
  Flex,
  ArrowDownRightIcon,
  IconButton,
  CloseIcon,
  type BoxProps,
  Box,
} from '@frontend/components';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import { getQueryBuilderCascaderInfo } from '../utils/cascader';
import {
  type GroupWithId,
  type QueryWithId,
  buildQueryFromCascaderValue,
} from '../utils/queryBuilding';
import { FilterQueryComponent } from './FilterQueryComponent';

export const QueryWrapper = ({
  children,
  ...props
}: { children: React.ReactNode } & BoxProps) => (
  <Flex
    align="flex-start"
    gap={1}
    wrap="wrap"
    className="query-wrapper"
    {...props}
  >
    <ArrowDownRightIcon
      position="relative"
      top={1}
      boxSize={2.5}
      color="feature.secondary"
      flexShrink={0}
    />
    {children}
  </Flex>
);

type FilterQueryWrappedProps = {
  query: QueryWithId;
  group: GroupWithId;
  queryIndex: number;
  signalIndex: number;
  groupIndex: number;
  isReadOnly: boolean;
  onClearQuery: (queryIndex: number) => void;
  onQueryValueChange: (queryIndex: number, value: QueryWithId) => void;
  unit: PartFileUnits;
  hideRemoveButton?: boolean;
} & BoxProps;

export const FilterQueryWrapped = ({
  query,
  group,
  queryIndex,
  signalIndex,
  groupIndex,
  isReadOnly,
  onClearQuery,
  onQueryValueChange,
  unit,
  hideRemoveButton,
  ...props
}: FilterQueryWrappedProps) => {
  const handleClearQuery = useCallback(() => {
    onClearQuery(queryIndex);
  }, [onClearQuery, queryIndex]);

  const handleQueryValueChange = useCallback(
    (value: QueryWithId) => {
      onQueryValueChange(queryIndex, value);
    },
    [onQueryValueChange, queryIndex],
  );

  const { configMap, valueTypeToAvailableUnits } = useReqsReviewConfigContext();

  const handleCascaderChange = useCallback(
    (value: string[]) => {
      if (!configMap) return;

      const nextQuery = buildQueryFromCascaderValue({
        nextCascaderValue: value,
        configMap,
        currentQuery: query,
        valueTypeToAvailableUnits,
      });

      if (nextQuery) {
        handleQueryValueChange(nextQuery);
      }
    },
    [configMap, query, handleQueryValueChange, valueTypeToAvailableUnits],
  );

  const memoizedQueryData = useMemo(() => {
    if (!configMap) return null;

    const fullPath = [group.documentPath, ...query.fieldName].join('.');
    const queryConfig = configMap[fullPath];
    if (!queryConfig) {
      console.warn(`Config not found for fullPath ${fullPath}`);
      return null;
    }

    const availableUnits = queryConfig.valueType
      ? valueTypeToAvailableUnits[queryConfig.valueType]
      : [];
    const selectOptions = queryConfig.selectOptions ?? [];

    const cascaderInfo = getQueryBuilderCascaderInfo({
      group,
      query,
      configMap,
    });
    if (!cascaderInfo) {
      console.warn(`Cascader info not found for query ${query.id}`);
      return null;
    }

    return {
      queryConfig,
      availableUnits,
      selectOptions,
      ...cascaderInfo,
    };
  }, [configMap, group, query, valueTypeToAvailableUnits]);

  if (!memoizedQueryData) {
    return null;
  }

  const {
    queryConfig,
    availableUnits,
    selectOptions,
    cascaderOptions,
    cascaderValue,
  } = memoizedQueryData;

  return (
    <QueryWrapper {...props}>
      <FilterQueryComponent
        signalIndex={signalIndex}
        groupIndex={groupIndex}
        queryIndex={queryIndex}
        query={query}
        config={queryConfig}
        availableUnits={availableUnits}
        selectOptions={selectOptions}
        isDisabled={isReadOnly}
        onChange={handleQueryValueChange}
        cascaderOptions={cascaderOptions}
        onCascaderChange={handleCascaderChange}
        cascaderValue={cascaderValue}
        unit={unit}
      />
      {!hideRemoveButton ? (
        <IconButton
          flexShrink={0}
          size="sm"
          m={0}
          mt={0.5}
          onClick={handleClearQuery}
          aria-label="Remove query"
          className="filter-query-remove-button"
        >
          <CloseIcon />
        </IconButton>
      ) : (
        <Box width={3} className="filter-query-remove-button-spacer" />
      )}
    </QueryWrapper>
  );
};
